import { componentFactoryOf } from 'vue-tsx-support';

import {
  JtnUiBadge,
  JtnUiBtn,
  JtnUiMobileContextMenu,
  JtnUiShareRedesign,
  JtnUiTooltip
} from '@jtnews/jtn-ui';
import { injectStylesMixin } from '@jtnews/shared';

import styles from './record-mobile-sticky-panel.styles.scss?module';

type SocialType = 'vk' | 'fb' | 'twitter' | 'ok' | 'telegram';

interface Events {
  onClickedBookmark: void;
  onShareRecord: SocialType;
  onClickComments: void;
  onLinkCopied: void;
}

type ComponentData = {
  isShowShareMenu: boolean;
  isBookmarkProcessing: boolean;
  isShowTooltip: boolean;
  isHidePanel: boolean;
};

const AUTO_CLOSE_TOOLTIP_TIME = 3000;

export default componentFactoryOf<Events>()
  .mixin(injectStylesMixin(styles))
  .create({
    name: 'RecordMobileStickyPanel',
    props: {
      isCommentsAllowed: {
        type: Boolean,
        default: false
      },
      commentsUrl: {
        type: String,
        default: ''
      },
      totalComments: {
        type: Number,
        default: 0
      },
      isInBookmarks: {
        type: Boolean,
        default: false
      },
      bookmarksUrl: {
        type: String,
        default: ''
      }
    },
    data(): ComponentData {
      return {
        isShowShareMenu: false,
        isBookmarkProcessing: false,
        isShowTooltip: false,
        isHidePanel: true
      };
    },
    computed: {
      bookmarkBtnTitle(): string {
        return this.isInBookmarks ? 'в закладках' : 'сохранить в закладки';
      },
      currentUrl(): string {
        return typeof window !== 'undefined' ? window.location.href : '';
      }
    },
    watch: {
      isInBookmarks(value) {
        if (value && this.isBookmarkProcessing) {
          this.isShowTooltip = true;
          this.isBookmarkProcessing = false;

          setTimeout(() => {
            this.closeTooltip();
          }, AUTO_CLOSE_TOOLTIP_TIME);
        }
      }
    },
    mounted() {
      window.addEventListener('scroll', () => this.onScrollPage());
    },
    methods: {
      showShareMenu() {
        this.isShowShareMenu = true;
      },
      closeShareMenu() {
        this.isShowShareMenu = false;
      },
      clickBookmarkBtn() {
        this.isBookmarkProcessing = true;
        this.$emit('clickedBookmark');
      },
      closeTooltip() {
        this.isShowTooltip = false;
      },
      shareRecord(socialName: string) {
        this.$emit('shareRecord', socialName);
      },
      emitLinkCopied() {
        this.$emit('linkCopied');
      },
      onScrollPage() {
        const pageYOffset = window.pageYOffset || document.documentElement.scrollTop;
        const heightScreen = window.innerHeight;

        if (pageYOffset > heightScreen) {
          this.isHidePanel = false;
        }
      },
      clickComments() {
        this.$emit('clickComments');
      }
    },
    render() {
      return (
        <div
          class={[styles.wrapper, this.isHidePanel ? styles.hide : '']}
          data-sticky-id="bottomStickyElement"
          data-sticked={!this.isHidePanel}
        >
          <div class={styles.inner}>
            {this.isCommentsAllowed && (
              <JtnUiBtn
                class={styles.actionBtn}
                href={this.commentsUrl}
                nativeOnClick={() => this.clickComments()}
              >
                <svg class={styles.btnIcon} width={24} height={24}>
                  <use xlinkHref="/dist/legacy/svg-sprites/jtn-critical.42b7545660e4f467e75d4b37a02533e6.svg#jtn-critical-discuss"></use>
                </svg>
                <span class={styles.btnText}>Комментарии</span>
                <JtnUiBadge
                  class={styles.badge}
                  size="button"
                  content={this.totalComments}
                />
              </JtnUiBtn>
            )}
            <JtnUiBtn
              class={[styles.actionBtn, this.isCommentsAllowed ? styles.smallBtn : '']}
              theme="white"
              onClick={() => this.clickBookmarkBtn()}
            >
              <template slot="left">
                {this.isInBookmarks ? (
                  <svg width={24} height={24} class={styles.bookmarkActiveIcon}>
                    <use xlinkHref="/dist/legacy/svg-sprites/jtn-common.f7875ac1d2b1e964bfa6c21ef5757c8a.svg#jtn-common-bookmark-solid"></use>
                  </svg>
                ) : (
                  <svg width={24} height={24}>
                    <use xlinkHref="/dist/legacy/svg-sprites/jtn-critical.42b7545660e4f467e75d4b37a02533e6.svg#jtn-critical-bookmark-outline"></use>
                  </svg>
                )}
              </template>

              {!this.isCommentsAllowed ? this.bookmarkBtnTitle : ''}
            </JtnUiBtn>
            <JtnUiBtn
              class={[styles.actionBtn, styles.smallBtn]}
              theme="white"
              nativeOnClick={() => this.showShareMenu()}
            >
              <template slot="left">
                <svg width={24} height={24}>
                  <use xlinkHref="/dist/legacy/svg-sprites/jtn-critical.42b7545660e4f467e75d4b37a02533e6.svg#jtn-critical-share"></use>
                </svg>
              </template>
            </JtnUiBtn>
            {this.isShowShareMenu && (
              <JtnUiMobileContextMenu onCloseClicked={() => this.closeShareMenu()}>
                <JtnUiShareRedesign
                  class={styles.shareBlock}
                  copyLink={this.currentUrl}
                  isInOneLine={true}
                  onShared={(event: string) => this.shareRecord(event)}
                  onLinkCopied={() => this.emitLinkCopied()}
                >
                  <span slot="caption">Поделиться</span>
                </JtnUiShareRedesign>
              </JtnUiMobileContextMenu>
            )}
            {this.isShowTooltip && (
              <JtnUiTooltip
                class={styles.tooltip}
                onClick={() => this.closeTooltip()}
                onClickOutside={() => this.closeTooltip()}
              >
                <div class={styles.tooltipContent}>
                  <svg class={[styles.icon, styles.bookmarkIcon]} width={48} height={48}>
                    <use xlinkHref="/dist/legacy/svg-sprites/jtn-common.f7875ac1d2b1e964bfa6c21ef5757c8a.svg#jtn-common-bookmark-active"></use>
                  </svg>
                  <div class={styles.bookmarkMessage}>
                    Материал сохранен
                    <br />
                    <a href={this.bookmarksUrl}>в закладки</a>
                  </div>
                  <a class={[styles.icon, styles.arrowIcon]} href={this.bookmarksUrl}>
                    <svg width="24" height="24">
                      <use xlinkHref="/dist/legacy/svg-sprites/jtn-critical.42b7545660e4f467e75d4b37a02533e6.svg#jtn-critical-arrow-line-right"></use>
                    </svg>
                  </a>
                </div>
              </JtnUiTooltip>
            )}
          </div>
        </div>
      );
    }
  });
